/**
 * A valuer's reports: one row per report — address, submitted time, a live "waiting for"
 * timer, a status chip, and an at-a-glance summary so a clean report reads as clean
 * instantly.
 */
import { useMemo } from 'react';
import type { CheckResultRow, ErrorFindingRow, ReportRow, RiskResultRow, ValuerRow } from '../../reviewer/types';
import { deadlineBucket, summariseReport, type DeadlineBucket } from '../../reviewer/aggregate';
import { formatDate } from '../../lib/status';
import { Tile, Button, activatable } from '../../components/ui';
import { StatusChip } from '../../components/reviewer/StatusChip';
import { WaitingTimer } from '../../components/reviewer/WaitingTimer';
import { AddToCalendar } from '../../components/reviewer/AddToCalendar';

/** Restrained: only the marker text takes colour (mark--fail/mark--flag), reusing the
 * existing status-mark palette rather than inventing a new one. */
function deadlineLabelClass(bucket: DeadlineBucket): string {
  if (bucket === 'overdue') return 'mark mark--fail';
  if (bucket === 'due_today') return 'mark mark--flag';
  return 'muted';
}

function deadlineLabelPrefix(bucket: DeadlineBucket): string {
  if (bucket === 'overdue') return 'Overdue — needed by ';
  if (bucket === 'due_today') return 'Due today — needed by ';
  return 'Needed by ';
}

interface ValuerReportsProps {
  valuer: ValuerRow | null;
  reports: ReportRow[];
  checkResults: CheckResultRow[];
  riskResults: RiskResultRow[];
  errorFindings: ErrorFindingRow[];
  onBack: () => void;
  onOpenReport: (reportId: string) => void;
}

export function ValuerReports({
  valuer,
  reports,
  checkResults,
  riskResults,
  errorFindings,
  onBack,
  onOpenReport,
}: ValuerReportsProps) {
  const rows = useMemo(
    () =>
      reports
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((report) => ({ report, summary: summariseReport(report.id, checkResults, riskResults, errorFindings) })),
    [reports, checkResults, riskResults, errorFindings],
  );

  return (
    <main className="main main--wide">
      <div className="page-head">
        <div>
          <Button variant="ghost" onClick={onBack}>
            ← All valuers
          </Button>
          <h1 className="page-title">{valuer?.displayName ?? 'Valuer'}</h1>
        </div>
      </div>

      {rows.length === 0 ? (
        <Tile className="tile--pad-lg">
          <div className="empty">
            <div className="empty__title">No reports yet</div>
            <p className="empty__body">Nothing has been uploaded for this valuer yet.</p>
          </div>
        </Tile>
      ) : (
        <div className="report-cards">
          {rows.map(({ report, summary }) => (
            <Tile key={report.id} className="report-card" {...activatable(() => onOpenReport(report.id))}>
              <div className="report-card__top">
                <div className="report-card__addr">{report.addressRaw || 'Address not recorded'}</div>
                <StatusChip status={report.status} />
              </div>
              <div className="report-card__meta">
                <span className="muted">Submitted {formatDate(report.submittedAt ?? report.firstSeenAt)}</span>
                {report.status !== 'done' ? (
                  <span className="muted">
                    · waiting <WaitingTimer since={report.submittedAt ?? report.firstSeenAt} />
                  </span>
                ) : null}
                {report.neededByAt ? (
                  <span className={deadlineLabelClass(deadlineBucket(report.neededByAt))}>
                    · {deadlineLabelPrefix(deadlineBucket(report.neededByAt))}
                    {formatDate(report.neededByAt)}
                  </span>
                ) : null}
              </div>
              <div className="report-card__summary">
                {summary.clean && (report.status === 'checked' || report.status === 'reviewing' || report.status === 'done') ? (
                  <span className="mark mark--pass pill">Clean</span>
                ) : (
                  <>
                    {summary.failCount > 0 ? <span className="mark mark--fail pill">{summary.failCount} fail</span> : null}
                    {summary.flagCount > 0 ? <span className="mark mark--flag pill">{summary.flagCount} flag</span> : null}
                    {summary.riskFlagCount > 0 ? (
                      <span className="mark mark--flag pill">{summary.riskFlagCount} risk</span>
                    ) : null}
                    {summary.errorCount > 0 ? (
                      <span className="mark mark--na pill">{summary.errorCount} error{summary.errorCount === 1 ? '' : 's'}</span>
                    ) : null}
                  </>
                )}
              </div>
              <div className="report-card__actions">
                <AddToCalendar
                  reportId={report.id}
                  addressRaw={report.addressRaw}
                  valuerName={valuer?.displayName ?? 'Valuer'}
                />
              </div>
            </Tile>
          ))}
        </div>
      )}
    </main>
  );
}
